import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';

import { ApiUrlService } from './api-url.service';
import {
  AdminChatMessageSearchResponse,
  AdminChatMessage,
  AdminChatMessagesResponse,
  AdminConversationListResponse,
  AdminMarkReadResponse,
  AdminMarkUnreadResponse,
  AdminSendMessageRequest,
  AdminUnreadCountByUserResponse,
  AdminUnreadCountResponse,
  AttachmentDownloadUrlResponse,
  ChatAttachmentCreate,
  ChatMetadata,
  buildMessageType,
  hasValidChatContent
} from '../models/chat';

@Injectable({
  providedIn: 'root'
})
export class AdminChatService {
  constructor(private apiUrl: ApiUrlService, private http: HttpClient) {}

  public obtenerTotalNoLeidos(): Observable<AdminUnreadCountResponse> {
    return this.http.get<AdminUnreadCountResponse>(this.apiUrl.adminChatUnreadCount);
  }

  public obtenerNoLeidosPorUsuario(): Observable<AdminUnreadCountByUserResponse> {
    return this.http.get<AdminUnreadCountByUserResponse>(this.apiUrl.adminChatUnreadCountByUser);
  }

  public obtenerConversaciones(): Observable<AdminConversationListResponse> {
    return this.http.get<AdminConversationListResponse>(this.apiUrl.adminChatConversations);
  }

  public obtenerMensajes(
    idUsuario: string,
    limit = 30,
    cursorId?: number | null
  ): Observable<AdminChatMessagesResponse> {
    let params = new HttpParams()
      .set('id_usuario', idUsuario)
      .set('limit', String(limit));

    if (cursorId !== null && cursorId !== undefined) {
      params = params.set('cursor_id', String(cursorId));
    }

    return this.http.get<AdminChatMessagesResponse>(this.apiUrl.adminChatMessages, { params });
  }

  public buscarMensajes(idUsuario: string, query: string): Observable<AdminChatMessageSearchResponse> {
    const params = new HttpParams()
      .set('id_usuario', idUsuario)
      .set('query', query);

    return this.http.get<AdminChatMessageSearchResponse>(this.apiUrl.adminChatMessagesSearch, { params });
  }

  public enviarMensaje(idUsuario: string, payload: AdminSendMessageRequest): Observable<AdminChatMessage> {
    const params = new HttpParams().set('id_usuario', idUsuario);
    return this.http.post<AdminChatMessage>(this.apiUrl.adminChatMessages, payload, { params });
  }

  public enviarMensajeConAdjuntos(
    idUsuario: string,
    files: File[],
    bodyText?: string | null,
    idRepliedMessage?: number | null,
    metadataJson?: ChatMetadata | null
  ): Observable<AdminChatMessage> {
    const params = new HttpParams().set('id_usuario', idUsuario);
    const formData = this.crearFormDataAdjuntos(files, bodyText, idRepliedMessage, metadataJson);
    return this.http.post<AdminChatMessage>(this.apiUrl.adminChatMessagesWithAttachments, formData, { params });
  }

  public enviarTexto(
    idUsuario: string,
    bodyText: string,
    idRepliedMessage?: number | null,
    metadataJson?: ChatMetadata | null
  ): Observable<AdminChatMessage> {
    const payload = this.crearPayloadMensaje(bodyText, [], idRepliedMessage, metadataJson);
    return this.enviarMensaje(idUsuario, payload);
  }

  public editarMensaje(idMessage: number, bodyText: string): Observable<AdminChatMessage> {
    return this.http.patch<AdminChatMessage>(
      this.apiUrl.adminChatMessage(idMessage),
      { body_text: bodyText }
    );
  }

  public marcarConversacionLeida(idUsuario: string): Observable<AdminMarkReadResponse> {
    const params = new HttpParams().set('id_usuario', idUsuario);
    return this.http.post<AdminMarkReadResponse>(this.apiUrl.adminChatRead, null, { params });
  }

  public marcarConversacionNoLeida(idUsuario: string): Observable<AdminMarkUnreadResponse> {
    const params = new HttpParams().set('id_usuario', idUsuario);
    return this.http.post<AdminMarkUnreadResponse>(this.apiUrl.adminChatUnread, null, { params });
  }

  public obtenerUrlDescargaAdjunto(idAttachment: number): Observable<AttachmentDownloadUrlResponse> {
    return this.http.get<AttachmentDownloadUrlResponse>(
      this.apiUrl.adminChatAttachmentDownloadUrl(idAttachment)
    );
  }

  public obtenerContenidoAdjunto(idAttachment: number): Observable<Blob> {
    return this.http.get(this.apiUrl.adminChatAttachmentContent(idAttachment), {
      responseType: 'blob'
    });
  }

  public crearPayloadMensaje(
    bodyText?: string | null,
    attachments: ChatAttachmentCreate[] = [],
    idRepliedMessage?: number | null,
    metadataJson?: ChatMetadata | null
  ): AdminSendMessageRequest {
    const normalizedText = bodyText?.trim() || null;

    if (!hasValidChatContent(normalizedText, attachments.length)) {
      throw new Error('El mensaje debe incluir texto o adjuntos ya subidos.');
    }

    return {
      body_text: normalizedText,
      message_type: buildMessageType(normalizedText, attachments.length),
      attachments,
      id_replied_message: idRepliedMessage ?? null,
      metadata_json: metadataJson ?? null
    };
  }

  public esPayloadMensajeValido(payload: AdminSendMessageRequest): boolean {
    return hasValidChatContent(payload.body_text, payload.attachments.length);
  }

  private crearFormDataAdjuntos(
    files: File[],
    bodyText?: string | null,
    idRepliedMessage?: number | null,
    metadataJson?: ChatMetadata | null
  ): FormData {
    const formData = new FormData();
    const normalizedText = bodyText?.trim();

    if (normalizedText) {
      formData.append('body_text', normalizedText);
    }

    if (idRepliedMessage !== null && idRepliedMessage !== undefined) {
      formData.append('id_replied_message', String(idRepliedMessage));
    }

    if (metadataJson) {
      formData.append('metadata_json', JSON.stringify(metadataJson));
    }

    files.forEach((file) => {
      formData.append('files', file, file.name);
    });

    return formData;
  }
}
